import { createTheme, PaletteOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    lower?: Palette["primary"];
  }
  interface PaletteOptions {
    lower?: PaletteOptions["primary"];
  }
}

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3A2ADA",
      light: "#574ad4",
      dark: "#2718BF",
      contrastText: "#fff",
    },
    secondary: {
      main: "#dc004e",
      light: "#ff5c8d",
      dark: "#9a0036",
      contrastText: "#fff",
    },
    success: {
      main: "#279C8A",
    },
    error: {
      main: "#800006",
    },
    warning: {
      main: "#A7000F",  
    },
    info: {
      main: "#E85800",
    },
    lower: {
      main: "#F2BF00",
    },
    background: {
      default: "#fbfbff",
      paper: "#ffffff",
    },
    text: {
      primary: "#1E2036",
      secondary: "#696595",
    },
    divider: "rgba(0, 0, 0, 0.12)",
  },
  typography: {
    fontFamily: "Manrope, sans-serif",
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#fbfbff",
          color: "#1E2036",
        },
        ".formik-error": {
          color: "#f44336",
          fontSize: "13px",
          fontWeight: "500",
        },
        // AG Grid Light Theme Styles
        ".ag-theme-material": {
          "--ag-background-color": "#ffffff",
          "--ag-foreground-color": "#1E2036",
          "--ag-header-background-color": "#E7E6F3",
          "--ag-odd-row-background-color": "#ffffff",
          "--ag-even-row-background-color": "#ffffff",
          "--ag-header-foreground-color": "#1E2036",
          "--ag-data-color": "#1E2036",
          "--ag-row-hover-color": "rgba(25, 118, 210, 0.04)",
          "--ag-selected-row-background-color": "rgba(25, 118, 210, 0.08)",
          "--ag-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-secondary-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-alpine-active-color": "#3A2ADA",
          "--ag-range-selection-border-color": "#3A2ADA",
          "--ag-input-border-color": "rgba(0, 0, 0, 0.23)",
          "--ag-input-focus-border-color": "#3A2ADA",
          "--ag-chip-background-color": "rgba(0, 0, 0, 0.08)",
          "--ag-row-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-cell-horizontal-border": "rgba(0, 0, 0, 0.12)",
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: "#ffffff",
          },
          "& .ag-row": {
            borderColor: "rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
            "&.ag-row-odd": {
              backgroundColor: "#ffffff",
            },
            "&.ag-row-even": {
              backgroundColor: "#ffffff",
            },
          },
          "& .ag-cell": {
            borderColor: "rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
            color: "#1E2036",
          },
          "& .ag-paging-panel": {
            color: "#1E2036",
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
          },
        },
        ".ag-popup, .ag-menu, .ag-filter-wrapper": {
          color: "#1E2036 !important",
          "& .ag-popup-child, & .ag-menu, & .ag-filter, & .ag-filter-wrapper, & .ag-simple-filter-body-wrapper, & .ag-filter-condition": {
            backgroundColor: "#ffffff !important",
            color: "#1E2036 !important",
            borderColor: "rgba(0, 0, 0, 0.12) !important",
          },
          "& input, & .ag-input-field-input, & select, & .ag-filter-select, & .ag-picker-field-wrapper": {
            backgroundColor: "#ffffff !important",
            color: "#1E2036 !important",
            borderColor: "rgba(0, 0, 0, 0.23) !important",
            borderRadius: "4px",
            "&::placeholder": {
              color: "#696595 !important",
              opacity: "1 !important",
            },
          },
          "& .ag-picker-field-display, & span, & div, & label, & .ag-set-filter-item": {
            color: "#1E2036 !important",
          },
          "& .ag-icon": {
            color: "#1E2036 !important",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#1E2036",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          "& .container": {
            backgroundColor: "#ffffff",
            color: "#1E2036",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            "& .header": {
              color: "#1E2036",
              "& h2": {
                color: "#1E2036",
              },
            },
            "& .list_data_container": {
              backgroundColor: "#ffffff",
              color: "#1E2036",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          backgroundImage: "none",
          color: "#1E2036",
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // Do NOT put purple hover/disabled on root — that made every Cancel/Unmap/
        // outlined button look like the primary CTA when hovered or focused.
        root: {
          borderRadius: "6px",
          textTransform: "none",
          fontSize: "12px",
        },
        contained: {
          "&.Mui-disabled": {
            backgroundColor: "#3A2ADA",
            color: "#ffffff",
            opacity: 0.5,
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#574ad4",
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "#9A0036",
          },
        },
        outlined: {
          "&:hover": {
            backgroundColor: "rgba(58, 42, 218, 0.04)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(58, 42, 218, 0.04)",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#1E2036",
        },
      },
    },
MuiTextField: {
  styleOverrides: {
    root: {
      "& .MuiInputBase-root": {
        "&:not(.MuiInputBase-multiline)": {
          height: "40px", // Set height to 40px
          minHeight: "40px",
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: "12px", // Slightly smaller for compact field
        color: "#696595",
        top: "-4px", // Adjust label vertical position
      },
      "& .MuiInputBase-input": {
        fontSize: "14px",
        fontWeight: "500",
        color: "#1E2036",
        padding: "10px 14px", // Adjust padding for 40px height
        height: "20px", // Ensures input is vertically centered
        boxSizing: "border-box",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        minHeight: "40px",
        "& fieldset": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "&:hover fieldset": {
          borderColor: "#3A2ADA",
        },
        "&.Mui-focused fieldset": {
          borderWidth: "2px", // Optional: highlight on focus
        },
      },
      "& .MuiFormHelperText-root": {
        marginLeft: "0px",
        // color: "#f44336 !important",
        fontSize: "12px",
        fontWeight: "500",
      },
      "& .MuiFilledInput-root": {
        backgroundColor: "#F4F6F8",
        minHeight: "40px",
      },
      "& .MuiFilledInput-root:hover": {
        backgroundColor: "#F4F6F8",
      },
    },
  },
},

  
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#1E2036",
          "&.error": {
            color: "#f44336",
            fontSize: "13px",
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: "#1E2036",
        },
        secondary: {
          color: "#696595",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#1E2036",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          color: "#1E2036",
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          color: "#1E2036",
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#3A2ADA",
      light: "#574ad4",
      dark: "#2718BF",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#9A0036",
      light: "#dc004e",
      dark: "#9a0036",
      contrastText: "#ffffff",
    },
    success: {
      main: "#279C8A",
    },
    error: {
      main: "#f44336",
    },
    background: {
      default: "#121212",
      paper: "#191C24"
    },
    text: {
      primary: "#ffffff",
      secondary: "#b3b3b3",
    },
    divider: "rgba(255, 255, 255, 0.12)",
  },
  typography: {
    fontFamily: "Manrope, sans-serif",
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#121212",
          color: "#ffffff",
        },
        ".formik-error": {
          color: "#ff7961",
          fontSize: "13px",
          fontWeight: "500",
        },
        // AG Grid Dark Theme Styles
        ".ag-theme-material": {
          "--ag-background-color": "#191C24",
          "--ag-foreground-color": "#ffffff",
          "--ag-header-background-color": "#2d2d2d",
          "--ag-odd-row-background-color": "#191C24",
          "--ag-even-row-background-color": "#191C24",
          "--ag-header-foreground-color": "#ffffff",
          "--ag-data-color": "#ffffff",
          "--ag-row-hover-color": "rgba(255, 255, 255, 0.08)",
          "--ag-selected-row-background-color": "rgba(255, 255, 255, 0.16)",
          "--ag-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-secondary-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-alpine-active-color": "#574ad4",
          "--ag-range-selection-border-color": "#574ad4",
          "--ag-input-border-color": "rgba(255, 255, 255, 0.23)",
          "--ag-input-focus-border-color": "#7b71e3",
          "--ag-chip-background-color": "rgba(255, 255, 255, 0.08)",
          "--ag-row-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-cell-horizontal-border": "rgba(255, 255, 255, 0.12)",
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: "#191C24",
          },
          "& .ag-row": {
            borderColor: "rgba(255, 255, 255, 0.12)",
            backgroundColor: "#191C24",
            "&.ag-row-odd": {
              backgroundColor: "#191C24",
            },
            "&.ag-row-even": {
              backgroundColor: "#191C24",
            },
          },
          "& .ag-cell": {
            borderColor: "rgba(255, 255, 255, 0.12)",
            backgroundColor: "#191C24",
            color: "#ffffff",
          },
          "& .ag-paging-panel": {
            color: "#ffffff",
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            backgroundColor: "#191C24",
          },
          "& .ag-tooltip": {
            backgroundColor: "#191C24 !important",
            color: "#ffffff !important",
          },
        },
        ".dark-theme .ag-popup, body.dark-theme .ag-popup, .dark-theme .ag-menu, body.dark-theme .ag-menu, .dark-theme .ag-filter-wrapper, body.dark-theme .ag-filter-wrapper, .dark-theme .ag-filter, body.dark-theme .ag-filter": {
          color: "#ffffff !important",
          "& .ag-popup-child, & .ag-menu, & .ag-filter, & .ag-filter-wrapper, & .ag-simple-filter-body-wrapper, & .ag-filter-condition": {
            backgroundColor: "#252935 !important",
            color: "#ffffff !important",
            borderColor: "#393E4B !important",
          },
          "& input, & .ag-input-field-input, & select, & .ag-filter-select, & .ag-picker-field-wrapper": {
            backgroundColor: "#191C24 !important",
            color: "#ffffff !important",
            borderColor: "#393E4B !important",
            borderRadius: "4px",
            "&::placeholder": {
              color: "rgba(255, 255, 255, 0.5) !important",
              opacity: "1 !important",
            },
          },
          "& .ag-picker-field-display, & span, & div, & label, & .ag-set-filter-item": {
            color: "#ffffff !important",
          },
          "& .ag-icon": {
            color: "#ffffff !important",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#191C24",
          color: "#ffffff",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          "& .container": {
            backgroundColor: "#191C24",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            "& .header": {
              color: "#ffffff",
              "& h2": {
                color: "#ffffff",
              },
            },
            "& .list_data_container": {
              backgroundColor: "#191C24",
              color: "#ffffff",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#191C24",
          backgroundImage: "none",
          color: "#ffffff",
          border: "1px solid rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          textTransform: "none",
          fontSize: "12px",
        },
        contained: {
          "&.Mui-disabled": {
            backgroundColor: "#574ad4",
            color: "#ffffff",
            opacity: 0.5,
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#7b71e3",
          },
        },
        outlined: {
          "&:hover": {
            backgroundColor: "rgba(123, 113, 227, 0.12)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1E1E1E",
          backgroundImage: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            color: "#b3b3b3",
          },
          "& .MuiInputBase-input": {
            color: "#ffffff",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
            "&:hover fieldset": {
              borderColor: "#7b71e3",
            },
          },
          "& .MuiFilledInput-root": {
            backgroundColor: "rgba(255, 255, 255, 0.09)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.13)",
            },
          },
          // Override browser autofill background for dark mode
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
            WebkitBoxShadow: "0 0 0px 1000px #191C24 inset !important",
            WebkitTextFillColor: "#ffffff !important",
            caretColor: "#ffffff",
            transition: "background-color 5000s ease-in-out 0s",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#ffffff",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: "#ffffff",
        },
        secondary: {
          color: "#b3b3b3",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#ffffff",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
          color: "#ffffff",
        },
        head: {
          color: "#b3b3b3",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          color: "#ffffff",
        },
        icon: {
          color: "#b3b3b3",
        },
        select: {
          "&:focus": {
            backgroundColor: "#1E1E1E",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          color: "#ffffff",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#424242",
          color: "#ffffff",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#191C24",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#191C24",
          color: "#ffffff",
          border: "1px solid rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: "#191C24",
          color: "#ffffff",
          border: "1px solid rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          "& .container": {
            backgroundColor: "#191C24",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            "& .header": {
              color: "#ffffff",
              "& h2": {
                color: "#ffffff",
              },
            },
            "& .list_data_container": {
              backgroundColor: "#191C24",
              color: "#ffffff",
            },
          },
        },
      },
    },
  },
});

export { lightTheme, darkTheme };